/**
 * Lógica pura de compras (CC-6: NUNCA usar macros lingui aquí).
 *
 * Una orden de compra (purchase_order) sigue el flujo:
 *   borrador (draft) → confirmada (confirmed) → recibida (received)
 *
 * Al recibir se genera una recepción (stock_picking tipo 'receipt') que sube el
 * on-hand. La recepción puede ser PARCIAL: cada línea acumula `received_qty`; si
 * lo recibido < lo pedido, queda un BACKORDER (el resto pendiente) y la orden
 * permanece 'confirmed'. Cuando todas las líneas están completas pasa a
 * 'received'.
 *
 * Cantidades en ENTEROS (unidades menores, factor 1000), igual que F1/F2.
 */

export type PoState = "draft" | "confirmed" | "received";

export const PO_STATES: readonly PoState[] = ["draft", "confirmed", "received"] as const;

export function isPoState(value: string): value is PoState {
  return (PO_STATES as readonly string[]).includes(value);
}

export function coercePoState(value: string | null | undefined): PoState {
  return value && isPoState(value) ? value : "draft";
}

/** ¿Se puede confirmar? Solo desde borrador. */
export function canConfirm(state: string | null | undefined): boolean {
  return coercePoState(state) === "draft";
}

/** ¿Se puede recibir (total o parcial)? Solo una orden confirmada con pendientes. */
export function canReceive(state: string | null | undefined): boolean {
  return coercePoState(state) === "confirmed";
}

/** Línea de PO (subset relevante para los cálculos). */
export interface PoLineLike {
  product_id: string | null;
  qty: number | null;
  price: number | null;
  received_qty: number | null;
}

/** Cantidad pendiente de recibir de una línea (>= 0). */
export function pendingQty(line: PoLineLike): number {
  const qty = line.qty ?? 0;
  const received = line.received_qty ?? 0;
  return Math.max(qty - received, 0);
}

/** ¿Quedan unidades pendientes en alguna línea? (backorder abierto) */
export function hasBackorder(lines: PoLineLike[]): boolean {
  return lines.some((l) => pendingQty(l) > 0);
}

/** Total pedido de la orden (Σ qty), en unidades menores. */
export function totalOrdered(lines: PoLineLike[]): number {
  return lines.reduce((acc, l) => acc + (l.qty ?? 0), 0);
}

/** Total recibido de la orden (Σ received_qty), en unidades menores. */
export function totalReceived(lines: PoLineLike[]): number {
  return lines.reduce((acc, l) => acc + (l.received_qty ?? 0), 0);
}

/**
 * Valor total de la orden (Σ qty × price), en unidades menores de dinero.
 * `qty` está escalado ×1000; `price` es por unidad entera, así que se divide por
 * el factor para no arrastrar la escala de cantidad al valor monetario.
 */
export function totalValue(lines: PoLineLike[], qtyScale: number): number {
  return lines.reduce((acc, l) => acc + Math.round(((l.qty ?? 0) * (l.price ?? 0)) / qtyScale), 0);
}

/** Una recepción a aplicar para una línea concreta (cantidad a recibir ahora). */
export interface ReceiptAllocation {
  productId: string;
  /** Cantidad a recibir en esta recepción (unidades menores, > 0). */
  qty: number;
  /** Costo de compra por unidad entera (para la capa de valoración AVCO). */
  unitCost: number;
  /** received_qty acumulado resultante tras aplicar esta recepción. */
  newReceivedQty: number;
}

/** Línea de PO identificada (con id) para planificar la recepción. */
export interface IdentifiedPoLine extends PoLineLike {
  id: string;
}

/**
 * Planifica una recepción a partir de las cantidades a recibir por línea. Si
 * `requested` no especifica una línea, recibe TODO lo pendiente de esa línea
 * (recepción total por defecto). Recorta cada cantidad al pendiente disponible
 * (no se puede recibir más de lo que falta). Ignora líneas sin pendiente.
 *
 * @param lines      líneas de la orden (con id, qty, price, received_qty).
 * @param requested  mapa lineId → cantidad a recibir ahora (unidades menores).
 *                   Si es undefined, se recibe todo lo pendiente de cada línea.
 */
export function planReceipt(
  lines: IdentifiedPoLine[],
  requested?: Map<string, number>,
): ReceiptAllocation[] {
  const allocations: ReceiptAllocation[] = [];
  for (const line of lines) {
    const productId = line.product_id;
    if (!productId) {
      continue;
    }
    const pending = pendingQty(line);
    if (pending <= 0) {
      continue;
    }
    const ask = requested ? (requested.get(line.id) ?? 0) : pending;
    const qty = Math.min(Math.max(ask, 0), pending);
    if (qty <= 0) {
      continue;
    }
    allocations.push({
      productId,
      qty,
      unitCost: line.price ?? 0,
      newReceivedQty: (line.received_qty ?? 0) + qty,
    });
  }
  return allocations;
}

/**
 * Estado resultante de la orden tras una recepción: 'received' si ya no quedan
 * pendientes, 'confirmed' (backorder abierto) en caso contrario.
 *
 * @param linesAfter líneas con su received_qty YA actualizado.
 */
export function resolvePoStateAfterReceipt(linesAfter: PoLineLike[]): PoState {
  return hasBackorder(linesAfter) ? "confirmed" : "received";
}
